import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import { Save as SaveIcon, Warehouse as WarehouseIcon } from '@mui/icons-material';
import { createInventoryCountRequest, getAvailableWarehouses } from '../../services/inventoryCountService';
import { Warehouse } from '../../types/warehouse';

const NewInventoryCountRequest: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  
  // بيانات النموذج
  const [warehouseId, setWarehouseId] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  
  // أخطاء التحقق
  const [warehouseError, setWarehouseError] = useState<string>('');

  // جلب قائمة المستودعات
  useEffect(() => {
    const fetchWarehouses = async () => {
      setLoading(true);
      try {
        const response = await getAvailableWarehouses();
        setWarehouses(response.warehouses || []);
      } catch (err) {
        console.error('Error fetching warehouses:', err);
        setError('حدث خطأ أثناء جلب قائمة المستودعات');
      } finally {
        setLoading(false);
      }
    };

    fetchWarehouses();
  }, []);

  // التحقق من صحة البيانات
  const validateForm = (): boolean => {
    let isValid = true;

    // التحقق من اختيار مستودع
    if (!warehouseId) {
      setWarehouseError('يرجى اختيار المستودع');
      isValid = false;
    } else {
      setWarehouseError('');
    }

    return isValid;
  };

  // إرسال النموذج
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // التحقق من صحة البيانات
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    setError(null);
    
    try {
      const response = await createInventoryCountRequest({
        warehouseId,
        notes: notes.trim() || undefined
      });

      if (response && response.success) {
        setSuccess(true);
        // الانتقال إلى صفحة تفاصيل الطلب بعد إنشائه بنجاح
        setTimeout(() => {
          navigate(`/inventory-count/${response.request._id}`);
        }, 1500);
      } else {
        setError('حدث خطأ أثناء إنشاء طلب الجرد');
      }
    } catch (err) {
      console.error('Error creating inventory count request:', err);
      setError('حدث خطأ أثناء الاتصال بالخادم');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      {/* رسالة النجاح */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          تم إنشاء طلب الجرد بنجاح. جاري الانتقال إلى صفحة التفاصيل...
        </Alert>
      )}

      {/* رسالة الخطأ */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper 
        elevation={3} 
        sx={{ 
          p: 3, 
          mb: 3, 
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <WarehouseIcon color="primary" sx={{ mr: 1 }} />
          <Typography variant="h6" component="h2">
            معلومات طلب الجرد
          </Typography>
        </Box>
        
        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          {/* اختيار المستودع */}
          <Grid item xs={12} md={6}>
            <FormControl 
              fullWidth 
              required 
              error={!!warehouseError}
              disabled={loading || submitting}
            >
              <InputLabel id="warehouse-select-label">المستودع</InputLabel>
              <Select
                labelId="warehouse-select-label"
                value={warehouseId}
                label="المستودع"
                onChange={(e) => setWarehouseId(e.target.value)}
              >
                {loading ? (
                  <MenuItem disabled>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    جاري تحميل المستودعات...
                  </MenuItem>
                ) : warehouses.length === 0 ? (
                  <MenuItem disabled>لا توجد مستودعات متاحة</MenuItem>
                ) : (
                  warehouses.map((warehouse) => (
                    <MenuItem key={warehouse._id} value={warehouse._id}>
                      {warehouse.name}
                    </MenuItem>
                  ))
                )}
              </Select>
              {warehouseError && <FormHelperText>{warehouseError}</FormHelperText>}
            </FormControl>
          </Grid>

          {/* ملاحظات */}
          <Grid item xs={12}>
            <TextField
              label="ملاحظات"
              multiline
              rows={3}
              fullWidth
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={submitting}
              InputProps={{ sx: { borderRadius: 1 } }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* أزرار الإجراءات */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button
          variant="outlined"
          onClick={() => navigate('/inventory-count')}
          disabled={submitting}
        >
          إلغاء
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          startIcon={submitting ? <CircularProgress size={20} /> : <SaveIcon />}
          disabled={submitting}
        >
          {submitting ? 'جاري الإنشاء...' : 'إنشاء طلب جرد'}
        </Button>
      </Box>
    </Box>
  );
};

export default NewInventoryCountRequest;